import React, { ReactNode } from "react";
import styled from "styled-components";

import {
  STEP_FLOW_CONTENT_MAX_WIDTH,
  STEP_FLOW_STEPPER_MAX_WIDTH,
  StepFlowAction,
  renderStepFlowAction,
} from "./stepFlowActions";

/**
 * ステップフロー（ウィザード）画面の共通シェル。
 *
 * お問い合わせ / プロジェクト作成 / 求人応募 / ステップ見積もり / オファー作成 など、
 * 「戻る + ステップナビ → タイトル → 入力 → 下部アクションバー」の画面で
 * 見え方を揃えるための土台。`Modal`（variant="fullscreen"）と `StepFlowLayout`
 * の双方がこのファイルの部品を使うので、ここを直せば全フローに反映される。
 *
 * レイアウト規約:
 * - ヘッダー: 左端に戻る（chevron_left）、中央にステップナビ（最大 480px）
 * - 本文: 中央寄せ、最大 740px（`.u-wrapper-reading` と同じ読み幅）
 * - フッター: 本文と同じ幅上限の中で 左=戻る / 右=次へ（＋中央の自由スロット）
 *
 * 定数・アクション型・ボタン描画は `stepFlowActions.tsx` を参照。
 */

// ===== ヘッダー =====

/**
 * 3 カラムグリッド。左右を同じ最小幅の 1fr にすることで、中央のステップナビが
 * 戻るボタンの有無に関係なく画面中央に来る。
 */
export const StepFlowHeaderBar = styled.header<{ $stepperMaxWidth: number }>`
  display: grid;
  grid-template-columns:
    minmax(40px, 1fr)
    minmax(0, ${({ $stepperMaxWidth }) => $stepperMaxWidth}px)
    minmax(40px, 1fr);
  align-items: center;
  gap: 8px;
  min-height: 64px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--soremo-border);
  flex-shrink: 0;
`;

const HeaderLeft = styled.div.attrs({
  className: "u-row u-items-center u-gap8",
})`
  min-width: 0;
`;

const HeaderCenter = styled.div.attrs({
  className: "u-row-center u-items-center",
})`
  min-width: 0;
`;

const HeaderRight = styled.div.attrs({
  className: "u-row u-items-center u-gap8",
})`
  min-width: 0;
  justify-content: flex-end;
`;

export const StepFlowBackButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  flex-shrink: 0;

  .material-symbols-rounded {
    font-size: 24px;
    color: var(--soremo-gray);
    transition: color 0.2s ease;
  }

  @media (hover: hover) {
    &:hover .material-symbols-rounded {
      color: var(--soremo-blue);
    }
  }
`;

export interface StepFlowHeaderProps {
  /** 左端の戻るボタン。渡さなければ表示しない（中央寄せは維持される） */
  onBack?: () => void;
  backLabel?: string;
  /** 戻るボタンの右に置く見出し（任意） */
  title?: ReactNode;
  /** 中央スロット。通常は `Stepper` */
  children?: ReactNode;
  /** 右端スロット（閉じる・ヘルプ等） */
  rightSlot?: ReactNode;
  /**
   * 中央スロットの幅上限。既定 480px。ステップ数が多い（5 以上が常態の）
   * フローでは "wide"（本文と同じ 740px）にする。
   */
  stepperWidth?: "default" | "wide";
}

export const StepFlowHeader: React.FC<StepFlowHeaderProps> = ({
  onBack,
  backLabel = "戻る",
  title,
  children,
  rightSlot,
  stepperWidth = "default",
}) => (
  <StepFlowHeaderBar
    $stepperMaxWidth={
      stepperWidth === "wide" ? STEP_FLOW_CONTENT_MAX_WIDTH : STEP_FLOW_STEPPER_MAX_WIDTH
    }
  >
    <HeaderLeft>
      {onBack && (
        <StepFlowBackButton type="button" onClick={onBack} aria-label={backLabel}>
          <span className="material-symbols-rounded">chevron_left</span>
        </StepFlowBackButton>
      )}
      {title}
    </HeaderLeft>
    <HeaderCenter>{children}</HeaderCenter>
    <HeaderRight>{rightSlot}</HeaderRight>
  </StepFlowHeaderBar>
);

// ===== 本文 =====

/** スクロール領域。フッターは in-flow なので下余白のハックは不要 */
export const StepFlowBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
`;

/** 中央寄せ・幅上限つきの本文コンテナ */
export const StepFlowContent = styled.div`
  width: 100%;
  max-width: ${STEP_FLOW_CONTENT_MAX_WIDTH}px;
  margin-inline: auto;
`;

// ===== フッター =====

export const StepFlowFooterBar = styled.footer`
  flex-shrink: 0;
  padding: 16px 24px;
  border-top: 1px solid var(--soremo-border);
  background: #fff;
`;

export type StepFlowFooterWidth = "content" | "wide" | "full";

const FOOTER_MAX_WIDTH: Record<StepFlowFooterWidth, string> = {
  content: `${STEP_FLOW_CONTENT_MAX_WIDTH}px`,
  /* .u-wrapper と同じ 1140px（LP など本文が広い画面用） */
  wide: "1140px",
  full: "none",
};

const FooterInner = styled.div<{ $single: boolean; $width: StepFlowFooterWidth }>`
  width: 100%;
  max-width: ${({ $width }) => FOOTER_MAX_WIDTH[$width]};
  margin-inline: auto;
  display: flex;
  align-items: center;
  justify-content: ${({ $single }) => ($single ? "flex-end" : "space-between")};
  gap: 8px;
`;

const FooterRight = styled.div.attrs({
  className: "u-row u-items-center u-gap16",
})``;

export interface StepFlowFooterProps {
  /** 左（戻る / キャンセル） */
  secondaryAction?: StepFlowAction;
  /** 右（次へ / 送信） */
  primaryAction?: StepFlowAction;
  /** 右ボタンの左隣の自由スロット（合計金額など） */
  children?: ReactNode;
  /** 構造化アクションの代わりに自由配置する場合 */
  footer?: ReactNode;
  size?: "large" | "medium";
  /** 幅上限: content=740px（既定） / wide=1140px（.u-wrapper と同じ） / full=上限なし */
  width?: StepFlowFooterWidth;
}

export const StepFlowFooter: React.FC<StepFlowFooterProps> = ({
  secondaryAction,
  primaryAction,
  children,
  footer,
  size = "large",
  width = "content",
}) => {
  const single = !footer && !secondaryAction;
  return (
    <StepFlowFooterBar>
      <FooterInner $single={single} $width={width}>
        {footer ?? (
          <>
            {secondaryAction && renderStepFlowAction(secondaryAction, "tertiary", "filled", size)}
            <FooterRight>
              {children}
              {primaryAction && renderStepFlowAction(primaryAction, "primary", "filled", size)}
            </FooterRight>
          </>
        )}
      </FooterInner>
    </StepFlowFooterBar>
  );
};
